import { useEffect, useState } from 'react';
import type { EnvironmentalAspectPayload, EnvironmentalCondition } from './types';
import { CONDITION_LABEL } from './aspectsUtils';

const CONDITIONS = Object.keys(CONDITION_LABEL) as EnvironmentalCondition[];

interface LegalRequirementOption {
  id: string;
  code: string;
  title: string;
}

interface AspectFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: EnvironmentalAspectPayload) => Promise<void>;
  initial?: Partial<EnvironmentalAspectPayload>;
  title: string;
  scaleMax: number;
  legalRequirements?: LegalRequirementOption[];
}

function defaultPayload(scaleMax: number): EnvironmentalAspectPayload {
  return {
    activity: '',
    aspect: '',
    impact: '',
    condition: 'normal',
    severity: Math.min(3, scaleMax),
    frequency: Math.min(3, scaleMax),
    legalRelevance: Math.min(2, scaleMax),
    stakeholderConcern: Math.min(2, scaleMax),
    controlMeasures: '',
  };
}

function ScoreInput({
  label,
  value,
  scaleMax,
  onChange,
}: {
  label: string;
  value: number;
  scaleMax: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="block text-sm space-y-1">
      <span>{label}</span>
      <input
        type="range"
        min={1}
        max={scaleMax}
        value={value}
        onChange={(e) => onChange(Number.parseInt(e.target.value, 10))}
        className="w-full"
      />
      <span className="text-xs text-muted-foreground">
        {value} / {scaleMax}
      </span>
    </label>
  );
}

export function AspectFormModal({
  open,
  onClose,
  onSubmit,
  initial,
  title,
  scaleMax,
  legalRequirements = [],
}: AspectFormModalProps) {
  const [form, setForm] = useState<EnvironmentalAspectPayload>(defaultPayload(scaleMax));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm({ ...defaultPayload(scaleMax), ...initial });
    setError(null);
  }, [open, initial, scaleMax]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        ...form,
        controlMeasures: form.controlMeasures?.trim() || null,
        legalRequirementId: form.legalRequirementId || null,
        lifecycleStage: form.lifecycleStage?.trim() || null,
      });
      onClose();
    } catch {
      setError('No se pudo guardar el aspecto ambiental.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
      <form
        className="bg-card border border-border rounded-lg w-full max-w-2xl p-6 space-y-4 my-8"
        onSubmit={handleSubmit}
      >
        <h2 className="text-lg font-semibold">{title}</h2>
        {error && <p className="text-sm text-destructive">{error}</p>}

        <label className="block text-sm space-y-1">
          <span>Actividad / proceso</span>
          <input
            className="input w-full"
            required
            maxLength={255}
            value={form.activity}
            onChange={(e) => setForm((prev) => ({ ...prev, activity: e.target.value }))}
            placeholder="Ej: Operación de caldera, transporte de residuos"
          />
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block text-sm space-y-1">
            <span>Aspecto ambiental</span>
            <input
              className="input w-full"
              required
              maxLength={500}
              value={form.aspect}
              onChange={(e) => setForm((prev) => ({ ...prev, aspect: e.target.value }))}
              placeholder="Ej: Emisión de gases, consumo de agua"
            />
          </label>
          <label className="block text-sm space-y-1">
            <span>Impacto ambiental</span>
            <input
              className="input w-full"
              required
              maxLength={500}
              value={form.impact}
              onChange={(e) => setForm((prev) => ({ ...prev, impact: e.target.value }))}
              placeholder="Ej: Contaminación atmosférica, agotamiento de recurso"
            />
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block text-sm space-y-1">
            <span>Condición</span>
            <select
              className="input w-full"
              value={form.condition}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  condition: e.target.value as EnvironmentalCondition,
                }))
              }
            >
              {CONDITIONS.map((value) => (
                <option key={value} value={value}>
                  {CONDITION_LABEL[value]}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm space-y-1">
            <span>Etapa del ciclo de vida</span>
            <input
              className="input w-full"
              maxLength={120}
              placeholder="Opcional"
              value={form.lifecycleStage ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, lifecycleStage: e.target.value }))}
            />
          </label>
        </div>

        {legalRequirements.length > 0 && (
          <label className="block text-sm space-y-1">
            <span>Requisito legal asociado</span>
            <select
              className="input w-full"
              value={form.legalRequirementId ?? ''}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  legalRequirementId: e.target.value || null,
                }))
              }
            >
              <option value="">Sin enlace</option>
              {legalRequirements.map((req) => (
                <option key={req.id} value={req.id}>
                  {req.code} — {req.title}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-border rounded-md p-3">
          <ScoreInput
            label="Severidad"
            value={form.severity}
            scaleMax={scaleMax}
            onChange={(severity) => setForm((prev) => ({ ...prev, severity }))}
          />
          <ScoreInput
            label="Frecuencia"
            value={form.frequency}
            scaleMax={scaleMax}
            onChange={(frequency) => setForm((prev) => ({ ...prev, frequency }))}
          />
          <ScoreInput
            label="Relevancia legal"
            value={form.legalRelevance}
            scaleMax={scaleMax}
            onChange={(legalRelevance) => setForm((prev) => ({ ...prev, legalRelevance }))}
          />
          <ScoreInput
            label="Preocupación de partes interesadas"
            value={form.stakeholderConcern}
            scaleMax={scaleMax}
            onChange={(stakeholderConcern) => setForm((prev) => ({ ...prev, stakeholderConcern }))}
          />
        </div>

        <label className="block text-sm space-y-1">
          <span>Medidas de control operacional</span>
          <textarea
            className="input w-full min-h-[80px]"
            maxLength={10000}
            placeholder="Procedimientos, controles, indicadores…"
            value={form.controlMeasures ?? ''}
            onChange={(e) => setForm((prev) => ({ ...prev, controlMeasures: e.target.value }))}
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}
